import { inject } from '@angular/core';
import { tap } from 'rxjs';
import {
  patchState,
  signalStore,
  withMethods,
} from '@ngrx/signals';
import { updateEntity, withEntities } from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { CellularFacadeService } from './cellular-facade.service';
// Self-contained, like every data-access lib: the store speaks the Orval-GENERATED
// types, NOT `@dgacontrol-frontend/core`. The app components bridge generated↔core.
import type {
  CellularV2SimcardsListParams,
  SimcardCreateRequestV2Request,
  PatchedSimcardUpdateRequestV2Request,
  SimcardV2,
} from './generated/types';

export const SimcardStore = signalStore(
  withEntities<SimcardV2>(),
  // Table: v2 cursor list (load = page 1, loadMore = next cursor). COMPONENT-DRIVEN
  // (dt-simcard calls load({}) on init).
  withCursorPagination<SimcardV2, CellularV2SimcardsListParams>(() => {
    const facade = inject(CellularFacadeService);
    return (query, cursor) =>
      facade.getSimcardsPage({ ...query, cursor: cursor ?? undefined });
  }),
  withMethods((store, facade = inject(CellularFacadeService)) => ({
    // v2 create → single `{simcard}`. Do NOT addEntity into a cursor list: the new row's
    // server-side sort position is unknown → inserting locally desyncs the cursor →
    // dup/missing rows on the next loadMore. Reset to page 1 via the cursor feature's
    // `load` (server-authoritative).
    createSimcard(body: SimcardCreateRequestV2Request) {
      return facade.createSimCard(body).pipe(
        tap(() => {
          store.load({});
        }),
      );
    },
    // Mint inventory sync → may create rows whose server-side sort position is
    // unknown → same cursor rationale as create: reset to page 1 (server-authoritative).
    syncMintSimcards() {
      return facade.syncMintSimcards().pipe(
        tap(() => {
          store.load({});
        }),
      );
    },
    // v2 update → single `{simcard}` → patch the existing row IN PLACE (idKey = `id`,
    // the default withEntities selectId; the path param `identity` is only the URL).
    // The cursor stays valid (no reorder).
    updateSimcard(body: PatchedSimcardUpdateRequestV2Request, identity: string) {
      return facade.updateSimCard(body, identity).pipe(
        tap((res) => {
          patchState(store, updateEntity({ id: res.simcard.id, changes: res.simcard }));
        }),
      );
    },
  })),
);
